/**
 * Drama / Series Management API Types
 *
 * These types mirror the backend `Drama` aggregate used for both single dramas
 * and series (`dramaType` = 'DRAMA' | 'SERIES').
 */

import type { VideoVariant, PreviewImageResponse } from './movie';

/**
 * Supported drama types from backend.
 */
export type DramaType = 'DRAMA' | 'SERIES' | string;

/**
 * Access level for a drama/series.
 */
export type DramaAccessLevel = 'FREE' | 'PREMIUM';

/**
 * Content status.
 */
export type DramaStatus = 'ACTIVE' | 'INACTIVE' | 'PENDING';

/**
 * Core drama/series response (summary rows from list endpoint).
 *
 * Matches `DramaResponse` from backend:
 * - dramaId (optional, internal drama primary key)
 * - contentId
 * - title
 * - description
 * - thumbnailUrl
 * - languageId
 * - languageName
 * - accessLevel
 * - status
 * - dramaType
 * - totalSeasons
 * - totalEpisodes
 * - episodeDuration (total seconds per episode)
 * - averageRating
 * - ratingCount
 * - uploadDate
 */
export interface Drama {
  // Some backends expose both dramaId and contentId; prefer dramaId for episode APIs if present.
  dramaId?: string;
  contentId: string;
  title: string;
  description: string;
  thumbnailUrl?: string | null;
  languageId: string;
  languageName: string;
  accessLevel: DramaAccessLevel;
  status: DramaStatus;
  dramaType: DramaType;
  totalSeasons: number;
  totalEpisodes: number;
  /** Total seconds per episode (API contract). */
  episodeDuration: number;
  /**
   * Content priority for sorting (smaller number => higher priority).
   * Backend may return null/undefined for older rows.
   */
  priority?: number | null;
  averageRating: number;
  ratingCount: number;
  uploadDate: string;
}

/**
 * Genre info in drama detail responses.
 * Mirrors backend `GenreDetailResponse`.
 */
export interface DramaGenre {
  genreId: string;
  name: string;
  nameMm: string;
  description?: string | null;
  iconUrl?: string | null;
  colorCode?: string | null;
  isPrimary?: boolean;
}

/**
 * Cast info attached to a drama/series.
 * Mirrors backend `CastDetailResponse`.
 */
export interface DramaCast {
  castId: string;
  name: string;
  nameMm?: string | null;
  profileImageUrl?: string | null;
  role?: string | null;
}

/**
 * Drama/Series detail response.
 *
 * Mirrors `DramaDetailResponse` from backend:
 * - drama
 * - genres
 * - primaryGenre
 * - casts
 * - videoVariants (content-level)
 * - hasReadyVideos
 */
export interface DramaDetailResponse {
  drama: Drama;
  genres: DramaGenre[];
  primaryGenre: DramaGenre | null;
  casts: DramaCast[];
  videoVariants: VideoVariant[];
  hasReadyVideos: boolean;
  previewImages?: PreviewImageResponse[];
}

/**
 * Paginated list data for dramas/series.
 *
 * Matches Page<DramaResponse> that backend returns as `data` for list endpoint.
 */
export interface DramasPage {
  items: Drama[];
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

/**
 * Query params for listing dramas/series.
 *
 * Backend:
 *  - page (0-based)
 *  - size
 *  - keyword (search)
 *  - dramaType (e.g. 'series')
 */
export interface DramasQueryParams {
  page?: number;
  size?: number;
  keyword?: string;
  dramaType?: string;
  accessLevel?: string;
  /** Content region: "myanmar" | "international" */
  contentRegion?: string;
}

/**
 * Create Drama/Series Request payload (JSON body inside multipart `data`
 * field or JSON-only endpoint).
 *
 * For series, always send `dramaType: 'SERIES'`.
 */
export interface CreateDramaRequest {
  title: string;
  description: string;
  languageId: string;
  dramaType: DramaType;
  totalSeasons: number;
  totalEpisodes: number;
  /** Total seconds per episode (API contract). */
  episodeDuration: number;
  initialGenreIds: string[];
  primaryGenreId: string;
  // Optional thumbnail URL when using JSON-only endpoint
  thumbnailUrl?: string;
}

/**
 * Update Drama/Series Request payload.
 *
 * Mirrors `DramaUpdateRequest` from backend.
 */
export interface UpdateDramaRequest {
  title?: string;
  description?: string;
  dramaType?: DramaType;
  totalSeasons?: number;
  totalEpisodes?: number;
  /** Total seconds per episode when updating. */
  episodeDuration?: number;
  accessLevel?: DramaAccessLevel;
  thumbnailUrl?: string;
  languageId?: string;
  initialGenreIds?: string[];
  primaryGenreId?: string;
  /**
   * Optional priority update (admin).
   */
  priority?: number;
}

/**
 * Assign cast to drama request.
 */
export interface AssignCastRequest {
  castId: string;
  role?: string;
}

/**
 * Response when assigning/updating cast relationship.
 * Minimal representation matching typical `ContentCastResponse`.
 */
export interface ContentCastResponse {
  contentId: string;
  castId: string;
  role?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Helper function to format date for display
 */
export const formatDateForDisplay = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Helper function to format datetime for display
 */
export const formatDateTimeForDisplay = (dateString: string | null | undefined): string => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

